import { ErrorMessage } from '@hookform/error-message';
import { isEmpty } from 'lodash';
import { Contact } from '~api/address_book';

import { Fragment, ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Add16, Bin16 } from '@ingrid/iconography/react';
import {
  Button,
  ButtonLink,
  Card,
  Checkbox,
  CheckboxLayout,
  FormControl,
  FormItem,
  IconButton,
  ListLayout,
  Modal,
  TextInput,
} from '@ingrid/luterkort-react';

import { CountrySelect } from '~src/components/CountrySelect/CountrySelect';
import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { useTranslation } from '~src/i18n/use-translation';
import { FormValues } from '~src/pages/settings/address-book/components/address-book-form';
import { Validate } from '~src/pages/settings/address-book/components/validate';
import { removeByIndex } from '~src/utils/array';

import { useAddresses } from './addresses-context';

type FormType = 'contact' | 'address';

interface Props {
  onSubmit: (data: FormValues, saveToAddressBook: boolean) => void;
  setIsOpen: (value: boolean) => void;
  defaultValues?: Contact;
  trigger: ReactNode;
  isOpen: boolean;
  type: FormType;
}

export function AddressForm({ onSubmit, trigger, defaultValues, isOpen, setIsOpen, type }: Props) {
  const { prefix } = useAddresses();
  const { t } = useTranslation();

  const [saveToAddressBook, setSaveToAddressBook] = useState(false);

  const { control, formState, handleSubmit, watch, register, reset, getFieldState, setValue } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: defaultValues ?? {},
  });

  const showSaveToAddressBookOption = isEmpty(defaultValues) || !(defaultValues?.id?.includes(prefix) ?? true);
  const addressLines = watch('address.address_lines') ?? [''];
  const disableAppendAddressLine = addressLines.length >= 4 || addressLines[addressLines.length - 1] === '';

  return (
    <FormControl.Root>
      <Modal.Root
        onOpenChange={o => {
          setIsOpen(o);
          if (!o) {
            reset();
          }
        }}
        open={isOpen}
      >
        <Modal.Trigger>{trigger}</Modal.Trigger>

        <Modal.Content>
          <FormControl.Form
            onSubmit={handleSubmit(data => onSubmit(data, saveToAddressBook))}
            style={{ display: 'none' }}
          />

          <Modal.Header title={defaultValues ? t('shipments.address.edit') : t('shipments.address.createNew')} />

          <Modal.Body>
            <Card.List css={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
              <FormItem.Root css={{ gridColumn: '1 / -1' }}>
                <FormItem.Label>{t('common.name')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.address?.name?.message}>
                  <TextInput aria-required="true" {...register('address.name', Validate().name)} />
                </FormItem.Control>
                <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="address.name" />
              </FormItem.Root>

              <ListLayout.Column style={{ display: 'flex', gap: '16px', gridColumn: '1 / -1' }}>
                {addressLines.map((_, index) => {
                  const addressLineFieldState = getFieldState(`address.address_lines.${index}`);

                  return (
                    <FormItem.Root style={{ width: '100%' }} key={`field-arr-${index}`}>
                      <FormItem.Label>{t('common.addressLineIndex', { index: index + 1 })}</FormItem.Label>
                      <FormItem.Control invalid={addressLineFieldState.invalid}>
                        <ListLayout.Stack>
                          <ListLayout.Row style={{ gap: '16px' }}>
                            {/* Cannot use Validate() here, because it counts as a hook, which react doesn't like. */}
                            {/* Conditionally rendering hooks is not allowed by React, which this would result in. */}
                            <TextInput
                              data-invalid={addressLineFieldState.invalid}
                              {...register(`address.address_lines.${index}`, {
                                required:
                                  index === 0 ? t('common.fieldRequired', { field: t('common.addressLine') }) : false,
                              })}
                            />

                            <IconButton
                              aria-label={t('common.removeItem', { item: 1 })}
                              onClick={() => setValue('address.address_lines', removeByIndex(addressLines, index))}
                              style={{ flexShrink: '0' }}
                              disabled={index === 0}
                              variant="secondary"
                            >
                              <Bin16 />
                            </IconButton>
                          </ListLayout.Row>
                        </ListLayout.Stack>
                      </FormItem.Control>

                      {addressLineFieldState.error?.message && (
                        <FormItem.ErrorText>{addressLineFieldState.error.message}</FormItem.ErrorText>
                      )}
                    </FormItem.Root>
                  );
                })}

                <div>
                  <ButtonLink
                    onClick={() => setValue('address.address_lines', [...addressLines, ''])}
                    disabled={disableAppendAddressLine}
                  >
                    <Add16 />
                    {t('common.addAddressLine')}
                  </ButtonLink>
                </div>
              </ListLayout.Column>

              <FormItem.Root css={{ gridColumn: '1 / -1' }}>
                <FormItem.Label>{`${t('common.doorCode')} (${t('common.optional')})`}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.address?.door_code?.message}>
                  <TextInput {...register('address.door_code')} />
                </FormItem.Control>
                <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="address.door_code" />
              </FormItem.Root>
              <FormItem.Root>
                <FormItem.Label>{t('common.postalCode')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.address?.postal_code?.message}>
                  <TextInput aria-required="true" {...register('address.postal_code', Validate().postal_code)} />
                </FormItem.Control>
                <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="address.postal_code" />
              </FormItem.Root>

              <FormItem.Root css={{ gridColumn: '2 / span 3' }}>
                <FormItem.Label>{t('common.city')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.address?.city?.message}>
                  <TextInput aria-required="true" {...register('address.city', Validate().city)} />
                </FormItem.Control>
                <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="address.city" />
              </FormItem.Root>

              <FormItem.Root css={{ gridColumn: '1 / span 2' }}>
                <FormItem.Label>{`${t('common.region')} (${t('common.optional')})`}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.address?.region?.message}>
                  <TextInput {...register('address.region')} />
                </FormItem.Control>
                <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="address.region" />
              </FormItem.Root>

              <FormItem.Root css={{ gridColumn: '3 / span 2' }}>
                <FormItem.Label>{t('common.country')}</FormItem.Label>
                <Controller
                  control={control}
                  name="address.country"
                  render={({ field }) => (
                    <FormItem.Control>
                      <CountrySelect
                        {...field}
                        invalid={!!formState.errors.address?.country?.message}
                        onChange={value => {
                          field.onChange(value);
                          field.onBlur();
                        }}
                      />
                    </FormItem.Control>
                  )}
                  rules={Validate().country}
                />
                <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="address.country" />
              </FormItem.Root>

              {type === 'contact' && (
                <Fragment>
                  <FormItem.Root css={{ gridColumn: '1 / -1' }}>
                    <FormItem.Label>{`${t('common.phone')} (${t('common.optional')})`}</FormItem.Label>
                    <FormItem.Control invalid={!!formState.errors?.phone?.message}>
                      <TextInput aria-required="true" {...register('phone', Validate().phone)} />
                    </FormItem.Control>
                    <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="phone" />
                  </FormItem.Root>

                  <FormItem.Root css={{ gridColumn: '1 / -1' }}>
                    <FormItem.Label>{`${t('common.email')} (${t('common.optional')})`}</FormItem.Label>
                    <FormItem.Control invalid={!!formState.errors?.email?.message}>
                      <TextInput aria-required="true" {...register('email', Validate().email)} />
                    </FormItem.Control>
                    <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="email" />
                  </FormItem.Root>
                </Fragment>
              )}

              {showSaveToAddressBookOption && (
                <CheckboxLayout.Row css={{ gridColumn: '1 / -1' }}>
                  <Checkbox
                    aria-label={t('shipments.address.updateInAddressBook')}
                    checked={saveToAddressBook}
                    id={'checkbox-save_in_address-book'}
                    onCheckedChange={e => e !== 'indeterminate' && setSaveToAddressBook(e)}
                  />
                  <CheckboxLayout.Text as="label" htmlFor={'checkbox-save_in_address-book'}>
                    {isEmpty(defaultValues)
                      ? t('shipments.address.saveInAddressBook')
                      : t('shipments.address.updateInAddressBook')}
                  </CheckboxLayout.Text>
                </CheckboxLayout.Row>
              )}
            </Card.List>
          </Modal.Body>

          <Modal.Footer>
            <Modal.CloseButton>
              <Button variant="secondary">{t('common.cancel')}</Button>
            </Modal.CloseButton>

            <FormControl.SubmitButton asChild>
              <Button>{t('shipments.address.saveAddress')}</Button>
            </FormControl.SubmitButton>
          </Modal.Footer>

          <FormLeaveBlocker formState={formState} />
        </Modal.Content>
      </Modal.Root>
    </FormControl.Root>
  );
}
